/**
 * Провайдеры приложения: клиент запросов и сессия.
 *
 * Фоновых перезапросов нет: изменения приходят по веб-сокету из realtime_events,
 * а опрос поверх живого потока — это второй источник правды и расхождения в цифрах
 * между вкладками (§8.1).
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../shared/api/client.js';
import { SessionProvider } from './auth-guard.js';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 30_000,
        // Повторять запрос при 4xx бессмысленно: ответ не изменится
        retry: (failureCount, error) =>
          !(error instanceof ApiError && error.status < 500) && failureCount < 2,
      },
      mutations: {
        // Метки и раздача идемпотентны на сервере, но автоповтор всё равно
        // не нужен: диспетчер должен увидеть отказ, а не тихую вторую попытку
        retry: false,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }): React.JSX.Element {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>{children}</SessionProvider>
    </QueryClientProvider>
  );
}
